/**
 * Access Time API Service
 *
 * Service for fetching timesheet data from the Access Time API.
 * This service routes requests through the backend (FE -> BE -> Access Time API) for security.
 * This service is used when a company has "access-time" configured as the timesheet product.
 */

import type { AccessTimeTimesheetEntry } from '@/types/access-time.types';
import { getApiBaseUrl, getDataMode } from './data-service';
import { apiFetch } from './api-error-handler';

/**
 * Fetch timesheet entries from the Access Time API via the backend
 * Routes through: Frontend -> Backend -> Access Time API
 *
 * @param projectCode - The project code to fetch timesheets for
 * @returns Array of AccessTimeTimesheetEntry objects or null if not available
 */
export async function fetchAccessTimeTimesheets(
  projectCode: string
): Promise<AccessTimeTimesheetEntry[] | null> {
  const dataMode = getDataMode();

  if (dataMode !== 'live') {
    return null;
  }

  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/accesstime/timesheets?projectCode=${encodeURIComponent(projectCode)}`;

    const data = await apiFetch<
      | AccessTimeTimesheetEntry[]
      | {
          items?: AccessTimeTimesheetEntry[];
          data?: AccessTimeTimesheetEntry[];
          $values?: AccessTimeTimesheetEntry[];
        }
    >(url, {
      method: 'GET',
    });

    if (Array.isArray(data)) {
      return data;
    }

    if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.items)) {
        return dataObj.items as AccessTimeTimesheetEntry[];
      }
      if (Array.isArray(dataObj.data)) {
        return dataObj.data as AccessTimeTimesheetEntry[];
      }
      if (Array.isArray(dataObj.$values)) {
        return dataObj.$values as AccessTimeTimesheetEntry[];
      }
      if (Array.isArray(dataObj.list)) {
        return dataObj.list as AccessTimeTimesheetEntry[];
      }
      if (Array.isArray(dataObj.value)) {
        return dataObj.value as AccessTimeTimesheetEntry[];
      }
      if (Array.isArray(dataObj.results)) {
        return dataObj.results as AccessTimeTimesheetEntry[];
      }
      if (Array.isArray(dataObj.records)) {
        return dataObj.records as AccessTimeTimesheetEntry[];
      }
    }

    return [];
  } catch (error) {
    console.error('Error fetching Access Time timesheets:', error);
    throw error;
  }
}
